import { Calendar } from 'lucide-react';
import { formatDisplayDate } from '@/utils/date';
import type { SurveyDetail } from '../types/survey.types';
import { SurveyPeriodStatus } from './SurveyPeriodStatus';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { SurveyStatusBadge } from './SurveyStatusBadge';

interface SurveyDetailHeaderProps {
  survey: SurveyDetail;
}

export function SurveyDetailHeader({ survey }: SurveyDetailHeaderProps) {
  const questionCount = survey.questions.length;

  return (
    <div className="flex flex-col gap-4 rounded-xl border bg-card p-6 shadow-sm">
      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-col gap-2 min-w-0">
          {survey.description ? (
            <p className="text-sm text-muted-foreground whitespace-pre-line wrap-break-word">
              {survey.description}
            </p>
          ) : null}
        </div>
        <SurveyStatusBadge status={survey.status} />
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Calendar className="size-4 shrink-0" />
          <span aria-label="Survey active period">
            <time dateTime={survey.startDate}>{formatDisplayDate(survey.startDate)}</time>
            {' to '}
            <time dateTime={survey.endDate}>{formatDisplayDate(survey.endDate)}</time>
          </span>
        </div>
        <SurveyPeriodStatus startDate={survey.startDate} endDate={survey.endDate} />
      </div>

      <StatCardGrid
        stats={[
          {
            key: 'questions',
            label: 'Questions',
            value: questionCount,
          },
          {
            key: 'respondents',
            label: 'Respondents',
            value: survey.respondentCount,
          },
          {
            key: 'responses',
            label: 'Responses',
            value: survey.responseCount,
          },
        ]}
      />
    </div>
  );
}
